import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Icon,
  ICONS,
  Popover,
  Typography,
  DeleteModal,
  PLATFORM_THEME_COLORS,
  Modal,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';
import {
  DocumentTypesEnum,
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
} from '@thinknet-gmbh/platform-common';


import { useDeleteEmployeeDocumentTemplate } from 'src/hooks/employees/delete-employee-document-template.hook';
import { useDownloadDocument } from 'src/hooks/documents-templates/get-document-template-preview-file.hook';
import { useDeleteDocumentTemplateById } from 'src/hooks/documents-templates/delete-document-template-by-id.hook';
import { usePermissionCheck } from 'src/hooks/permission-check.hook';
import { PreviewDocumentTemplateModal } from './preview-document-template-modal';

import { DOCUMENTS_LIST_MODALS } from './document-files-list.constants';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';

import { ICurrentTemplate, IDocumentFilesList } from './document-files-list.interfaces';
import { FileListWrapper, DocumentListWrapper } from './document-files-list.styles';

const isValidFileExtension = (filename?: string): boolean => {
  if (!filename) {
    return false;
  }
  const validExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.pdf'];
  return validExtensions.some(ext => filename.toLowerCase().endsWith(ext));
};

const DocumentFilesList = ({
  template,
  employeeId,
  index,
  documentId,
  isOwnData,
}: IDocumentFilesList) => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [currentTemplate, setCurrentTemplate] = useState<ICurrentTemplate | null>(null);
  const { checkRule } = usePermissionCheck(PLATFORM_PERMISSIONS.companyManagement);

  const canEdit = checkRule(PERMISSION_ACTIONS.update, accountId => accountId === createdBy);
  const canDelete = checkRule(PERMISSION_ACTIONS.delete, accountId => accountId === createdBy);
  const { _id: templateId, createdBy, title } = template;
  const isSettingsView = index !== undefined;

  const onClose = () => {
    setActiveModal(null);
    setCurrentTemplate(null);
  };

  const deleteEmployeeDocumentTemplate = useDeleteEmployeeDocumentTemplate({ onSuccess: onClose });
  const deleteDocumentTemplateById = useDeleteDocumentTemplateById({ onSuccess: onClose });
  const { downloadDocument, loading: documentLoading } = useDownloadDocument();

  const onDeleteSubmit = () => {
    if (employeeId) {
      return deleteEmployeeDocumentTemplate({
        variables: {
          payload: {
            memberId: employeeId,
            documentId,
            documentTemplateId: currentTemplate?._id,
          },
        },
      });
    }
    deleteDocumentTemplateById({
      variables: {
        documentTemplateId: currentTemplate?._id,
      },
    });
  };

  const onClickDownloadHandler = (type: DocumentTypesEnum, templateId: string) => {
    downloadDocument({
      variables: {
        documentTemplateId: templateId,
        memberId: employeeId,
        filetype: type,
      },
    });
    setCurrentTemplate({ _id: templateId });
  };

  const isCanPreviewTheTemplate = isValidFileExtension(template?.file?.filename);

  return (
    <DocumentListWrapper isWidth={isSettingsView}>
      {activeModal === DOCUMENTS_LIST_MODALS.previewTemplate && (
        <PreviewDocumentTemplateModal
          documentTemplateId={currentTemplate?._id as string}
          employeeId={employeeId}
          onClose={onClose}
          isMemberOwnDoc={!!template.memberId}
        />
      )}

      {activeModal === DOCUMENTS_LIST_MODALS.deleteTemplate && (
        <DeleteModal
          isOpened={Boolean(currentTemplate?._id)}
          onClose={onClose}
          onSubmit={onDeleteSubmit}
          translate={t}
          title={t('documents.deleteModal.title')}
          subTitle={t('documents.deleteModal.subtitle', {
            templateName: currentTemplate?.templateName,
          })}
        />
      )}

      <Modal isOpened={documentLoading} title={''} closable={false}>
        <Loader />
      </Modal>

      <Popover
        trigger={isSettingsView ? 'click' : 'hover'}
        placement={isSettingsView ? 'right' : 'rightTop'}
        visible={isSettingsView ? index === currentTemplate?.index : undefined}
        onVisibleChange={visible => {
          if (!visible && isSettingsView) onClose();
        }}
        elements={[
          {
            label: t('documents.buttons.previewTheTemplate'),
            icon: ICONS.show,
            key: ICONS.show,
            ...(!isCanPreviewTheTemplate && { color: PLATFORM_THEME_COLORS.grayVariations.murky }),
            handler: () => {
              if (isCanPreviewTheTemplate) {
                setCurrentTemplate({ _id: templateId, templateName: title });
                setActiveModal(DOCUMENTS_LIST_MODALS.previewTemplate);
              }
            },
          },
          ...(employeeId
            ? [
                ...(template.memberId
                  ? [
                      {
                        label: t('vacancies:buttons.download'),
                        icon: ICONS.downvote,
                        key: ICONS.downvote,
                        handler: () => {
                          const a = document.createElement('a');
                          a.href = template.file.location;
                          a.click();
                        },
                      },
                    ]
                  : [
                      {
                        label: `${t('documents.labels.downloadAs')} .pdf`,
                        icon: ICONS.downvote,
                        key: ICONS.downvote,
                        handler: () => onClickDownloadHandler(DocumentTypesEnum.PDF, templateId),
                      },
                      {
                        label: `${t('documents.labels.downloadAs')} .doc`,
                        icon: ICONS.downvote,
                        key: ICONS.downvote,
                        handler: () => onClickDownloadHandler(DocumentTypesEnum.DOC, templateId),
                      },
                    ]),
              ]
            : [
                {
                  label: t('documents.buttons.editTheTemplate'),
                  icon: ICONS.editAlt,
                  key: ICONS.editAlt,
                  ...(!canEdit && { color: PLATFORM_THEME_COLORS.grayVariations.murky }),
                  handler: () => {
                    if (canEdit) {
                      navigate(
                        `${ROUTES.settings}?type=${SETTINGS_PAGES.documents}&id=${templateId}`
                      );
                    }
                  },
                },
              ]),
          ...(!isOwnData
            ? [
                {
                  label: t('documents.buttons.deleteDocument'),
                  icon: ICONS.trash,
                  key: ICONS.trash,
                  color: canDelete
                    ? PLATFORM_THEME_COLORS.tone.red
                    : PLATFORM_THEME_COLORS.grayVariations.murky,
                  handler: () => {
                    if (canDelete) {
                      setCurrentTemplate({ _id: templateId, templateName: title });
                      setActiveModal(DOCUMENTS_LIST_MODALS.deleteTemplate);
                    }
                  },
                },
              ]
            : []),
        ]}
        key={templateId}
      >
        {isSettingsView ? (
          <FileListWrapper
            isOpened={currentTemplate?.index === index}
            onClick={() => setCurrentTemplate({ index })}
          >
            <Icon icon={ICONS.file} size={16} />
            <Typography>{title}</Typography>
          </FileListWrapper>
        ) : (
          <Icon
            icon={ICONS.dotsVerticalRounded}
            rightMargin={false}
            size={22}
            color={PLATFORM_THEME_COLORS.primary.grey}
          />
        )}
      </Popover>
    </DocumentListWrapper>
  );
};

export default DocumentFilesList;
